"use client";

/**
 * "You viewed this listing · 12 October · the price has dropped 4% since"
 * on the listing's own page (owner, 2026-10-13). Reads the account's /
 * device's viewed list (lib/viewed); the page records the visit in
 * progress as it loads, so the line talks about the visit before it, and
 * shows nothing on a first visit.
 */
import { Eye } from "lucide-react";
import { useLocale, useT } from "@/lib/locale-context";
import { changeSinceViewed, earlierVisit, useViewed } from "@/lib/viewed";
import { formatMonthDay } from "@/lib/blog-format";

export default function ViewedNote({ id, price }: { id: string; price: number | null }) {
  const t = useT();
  const { locale } = useLocale();
  const visit = earlierVisit(useViewed().get(id));
  if (!visit) return null;
  const d = new Date(visit.at);
  const days = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86_400_000);
  const when = days <= 0 ? t("viewed.history.today") : days === 1 ? t("viewed.history.yesterday") : formatMonthDay(d.getTime(), locale);
  const moved = changeSinceViewed(price, visit.price);
  return (
    <p className="mt-2 inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-600">
      <Eye className="h-4 w-4 text-gray-400" aria-hidden />
      <span>{t("viewed.page.seen")}</span>
      <span>· {when}</span>
      {visit.price != null && price != null && (
        moved == null ? (
          <span>· {t("viewed.page.same")}</span>
        ) : (
          <span className={`font-semibold ${moved < 0 ? "text-emerald-600" : "text-rose-600"}`}>
            · {(moved < 0 ? t("viewed.page.down") : t("viewed.page.up")).replace("{pct}", `${Math.abs(moved)}%`)}
          </span>
        )
      )}
    </p>
  );
}
